// Iter 543 (+ iter 545) — LEADERBOARD DESAIN BORDIR (Wawa vs Rizal, dsb).
//
// Mandat Owner:
//   • Kedua PIC melihat produktivitas satu sama lain untuk kompetisi sehat.
//   • Peringkat 1/2 berdasarkan total; tie = seri.
//   • Toggle periode: Hari Ini / 7 Hari / 30 Hari / Semua Riwayat.
//   • Filter sumber: Offline / Shopee / Gabungan — memengaruhi SEMUA angka.
//   • Default: 7 Hari × Gabungan.
//   • WIB. Hari tanpa pekerjaan = 0.
//   • Jangan hitung berdasarkan nama text saja (backend pakai operator id).
//   • Jika hasil 0, tampilkan nol + keterangan jelas (bukan dummy).
//   • "Semua Riwayat" (days=0): tampilkan ranking total; chart harian disembunyikan
//     (karena rentang terlalu lebar — fokus ranking kumulatif).
//
// Komponen dipakai di:
//   • warehouse_module.js (ShopeeEmbroiderySection) — section desain Shopee.
//   • production_queue.js (team=Bordir, offline) — section desain Offline.
// Props legacy `scopeTab` tetap diterima utk kompat; default scope leaderboard = "all".
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

import { DesainPerformaPanel } from "./bordir_performance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palet dua PIC: oranye (Wawa) & biru (Rizal). Mapping deterministik by index
// desainers[] dari backend (asc by name), bukan hardcode nama.
const BAR_COLORS = ["#f97316", "#2563eb", "#10b981", "#a855f7"];

function _shortDate(d) {
  if (!d || typeof d !== "string") return "";
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}`;
}

function RankBadge({ rank, size = 20 }) {
  if (!rank) return <span style={{ fontSize: 14, lineHeight: 1, opacity: .6 }} aria-label="Belum ada peringkat" title="Belum ada titik/desain">–</span>;
  const label = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;
  return <span style={{ fontSize: size, lineHeight: 1 }} aria-label={`Peringkat ${rank}`}>{label}</span>;
}

export function LeaderboardDesainBordir({ initialScope = "all", testIdPrefix = "leaderboard-bordir" }) {
  const [scope, setScope] = useState(initialScope);
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/shopee/embroidery/leaderboard`, {
        params: { scope, days }, withCredentials: true, timeout: 15_000,
      });
      setData(r?.data || null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setData(null); // sembunyi diam-diam utk non-desainer/non-owner
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Gagal memuat leaderboard");
      }
    } finally {
      setLoading(false);
    }
  }, [scope, days]);

  useEffect(() => { load(); }, [load]);

  // Chart data: array rows (per tanggal) dengan key = nama designer.
  const chartData = useMemo(() => {
    if (!data) return [];
    const designerNames = (data.designers || []).map(d => d.name);
    return (data.dates || []).map((d, i) => {
      const row = { date: d, label: _shortDate(d) };
      for (const dn of designerNames) {
        const des = (data.designers || []).find(x => x.name === dn);
        row[dn] = des?.series?.[i]?.count ?? 0;
      }
      return row;
    });
  }, [data]);

  const xTickFormatter = useCallback((val, i) => {
    if (days === 30) return (i % 3 === 0) ? val : "";
    return val;
  }, [days]);

  const designers = data?.designers || [];
  const totalAll = designers.reduce((n, d) => n + (d.total || 0), 0);

  return (
    <section className="leaderboard-bordir" data-testid={testIdPrefix}
             style={{
               borderRadius: 10, padding: "10px 12px", margin: "10px 0 14px",
               display: "flex", flexDirection: "column", gap: 10,
             }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 className="lb-title" data-testid={`${testIdPrefix}-title`}>
            Leaderboard Desain Bordir
          </h3>
          <p className="lb-sub">
            Kompetisi sehat antar desainer ·{" "}
            {days === 0 ? "Semua Riwayat" : days === 1 ? "Hari Ini" : `${days} hari`} (WIB) ·{" "}
            {scope === "all" ? "Gabungan" : scope === "shopee" ? "Shopee" : "Offline"}
          </p>
        </div>
        <div role="tablist" aria-label="Pilih periode" style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {[
            { v: 1, label: "Hari Ini" },
            { v: 7, label: "7 Hari" },
            { v: 30, label: "30 Hari" },
            { v: 0, label: "Semua Riwayat" },
          ].map(({ v, label }) => (
            <button key={v} type="button" role="tab" aria-selected={days === v}
              onClick={() => setDays(v)}
              data-testid={`${testIdPrefix}-period-${v}`}
              className={`lb-chip${days === v ? " active" : ""}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Filter sumber (scope): Offline / Shopee / Gabungan */}
      <div role="tablist" aria-label="Filter sumber data" style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {[
          { v: "all", label: "Gabungan" },
          { v: "shopee", label: "Shopee" },
          { v: "offline", label: "Offline" },
        ].map(({ v, label }) => (
          <button key={v} type="button" role="tab" aria-selected={scope === v}
            onClick={() => setScope(v)}
            data-testid={`${testIdPrefix}-scope-${v}`}
            className={`lb-chip sm${scope === v ? " active" : ""}`}>
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="lb-sub">Memuat…</div>}
      {err && <div className="warn-box small" data-testid={`${testIdPrefix}-err`}>{err}</div>}

      {!loading && !err && (
        <>
          {/* Ringkasan peringkat per-desainer */}
          <div style={{ display: "grid", gap: 6, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}
               data-testid={`${testIdPrefix}-ranking`}>
            {designers.length === 0 && (
              <div className="lb-empty">Belum ada desainer bordir aktif.</div>
            )}
            {designers.map((d, idx) => (
              <div key={d.id || idx}
                   data-testid={`${testIdPrefix}-row-${d.id || idx}`}
                   className="lb-row"
                   style={{
                     display: "flex", alignItems: "center", gap: 8,
                     padding: "6px 8px", borderRadius: 8,
                     borderLeft: `4px solid ${BAR_COLORS[idx % BAR_COLORS.length]}`,
                   }}>
                <RankBadge rank={d.rank} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="lb-row-name">
                    {d.name}
                  </div>
                  <div className="lb-row-sub">
                    {days === 0 ? "Total kumulatif" : `Rata-rata ${d.average}/hari`}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="lb-total" data-testid={`${testIdPrefix}-total-${d.id || idx}`}>
                    {d.total}
                  </div>
                  <div className="lb-total-unit">desain</div>
                </div>
              </div>
            ))}
          </div>

          {/* Diagram batang grouped per hari — disembunyikan utk days=0 */}
          {designers.length > 0 && days !== 0 && (
            <div style={{ width: "100%", height: totalAll === 0 ? 96 : 180, minWidth: 0 }} data-testid={`${testIdPrefix}-chart`}>
              {totalAll === 0 ? (
                <div className="lb-empty">
                  Belum ada desain selesai pada periode ini.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 6, bottom: 4, left: 0 }}>
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "currentColor" }}
                      tickLine={false}
                      axisLine={{ stroke: "currentColor", opacity: 0.3 }}
                      tickFormatter={xTickFormatter}
                      interval={0}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: "currentColor" }}
                      tickLine={false}
                      axisLine={{ stroke: "currentColor", opacity: 0.3 }}
                      width={24}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(100,116,139,0.1)" }}
                      wrapperStyle={{ outline: "none" }}
                      contentStyle={{
                        background: "rgba(15,23,42,0.95)",
                        border: "1px solid rgba(148,163,184,0.4)",
                        borderRadius: 6, padding: "6px 10px",
                        fontSize: 11, color: "#f8fafc",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                      }}
                      itemStyle={{ color: "#f8fafc" }}
                      labelStyle={{ color: "#cbd5e1", fontSize: 10, marginBottom: 2 }}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
                      formatter={(val, name) => [`${val} desain`, name]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                      iconType="square"
                      iconSize={10}
                    />
                    {designers.map((d, idx) => (
                      <Bar key={d.id || idx} dataKey={d.name} name={d.name}
                           fill={BAR_COLORS[idx % BAR_COLORS.length]} radius={[3, 3, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// Backward-compat alias — modul lama (iter 542) mengimpor ProduktivitasHarianChart.
// Agar tidak break import existing di warehouse_module.js & production_queue.js,
// expose nama lama sebagai leaderboard dgn initialScope sesuai scopeTab.
export function ProduktivitasHarianChart({ scopeTab = "all", testIdPrefix }) {
  const normalized = scopeTab === "shopee" ? "shopee" : scopeTab === "offline" ? "offline" : "all";
  const prefix = testIdPrefix || "leaderboard-bordir";
  return <>
    <DesainPerformaPanel testIdPrefix={`${prefix}-performa`} />
    <LeaderboardDesainBordir initialScope={normalized} testIdPrefix={prefix} />
  </>;
}

export default LeaderboardDesainBordir;
